import React, {
  FC,
  ReactNode,
  useState,
  useEffect,
  KeyboardEvent
} from 'react'
/** @jsx jsx */
import { jsx, css } from '@emotion/core'
import { SwitchTransition } from 'react-transition-group'
import { Button } from '@components/Button'
import { ChevronLeftIcon, ChevronRightIcon } from '@components/Icons'
import {
  SliderWrapper,
  PaginationContainer,
  Dot,
  CSSTransitionPane,
  TRANSITION_DELAY,
  pageTransitionName,
  btnStyles,
  backwardClassName,
  forwardClassName,
} from './Slider.elements'

const AUTO_ADVANCE_DELAY = 3000
const AUTO_ADVANCE_DELAY_TOUCHED = 6000

type Props = {
  slides: ReactNode[]
  autoTransition?: boolean
}

export const Slider: FC<Props> = ({
  slides,
  autoTransition,
}) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0)
  const [goingForward, setGoingForward] = useState<boolean>(false)
  const [timeoutID, setTimeoutID] = useState<number>()
  const [touched, setTouched] = useState<boolean>(false)

  const handleShowPrev = (manuallyTriggered?: boolean) => {
    const nextSlide = currentSlide === 0
      ? slides.length - 1
      : currentSlide - 1

    setCurrentSlide(nextSlide)
    setGoingForward(false)
    manuallyTriggered && setTouched(true)
  }

  const handleShowNext = (manuallyTriggered?: boolean) => {
    const nextSlide = currentSlide === slides.length - 1
      ? 0
      : currentSlide + 1

    setCurrentSlide(nextSlide)
    setGoingForward(true)
    manuallyTriggered && setTouched(true)
  }

  const handleSlideClick = (index: number) => () => {
    setGoingForward(index >= currentSlide)
    setCurrentSlide(index)
    setTouched(true)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.key === 'ArrowLeft' ? handleShowPrev(true) : handleShowNext(true)
    }
  }

  const resetAutoTransition = () => {
    if (!autoTransition) return
    // Clears any previous timeout and restart the auto advance.
    timeoutID && clearTimeout(timeoutID)
    autoTransition && autoAdvanceOnNextTick()
  }

  const autoAdvanceOnNextTick = () => {
    const id = setTimeout(
      handleShowNext,
      touched ? AUTO_ADVANCE_DELAY_TOUCHED : AUTO_ADVANCE_DELAY
    )
    setTimeoutID(id)
  }

  useEffect(() => {
    if (!autoTransition) return

    return () => {
      // Clear timeout on unmount.
      timeoutID && clearTimeout(timeoutID)
    }
  }, [])

  // Whenever the slide change, reset the auto transition to start it again.
  useEffect(resetAutoTransition, [currentSlide])

  return (
    <SliderWrapper className={goingForward ? forwardClassName : backwardClassName}>
      <SwitchTransition>
        <CSSTransitionPane
          key={currentSlide}
          classNames={pageTransitionName}
          timeout={TRANSITION_DELAY}
        >
          <div>{slides[currentSlide]}</div>
        </CSSTransitionPane>
      </SwitchTransition>

      <PaginationContainer>
        <li>
          <Button
            onClick={() => handleShowPrev(true)}
            onKeyDown={handleKeyDown}
            aria-label="Show previous slide"
            circled
            variant="text"
            css={btnStyles}
          >
            <ChevronLeftIcon />
          </Button>
        </li>

        {slides.map((_, index) => (
          <li key={index}>
            <Button
              onClick={handleSlideClick(index)}
              onKeyDown={handleKeyDown}
              aria-label={`Show slide #${index}`}
              circled
              variant="text"
              css={btnStyles}
            >
              <Dot
                key={index}
                isActive={index === currentSlide}
              />
            </Button>
          </li>
        ))}

        <li>
          <Button
            onClick={() => handleShowNext(true)}
            onKeyDown={handleKeyDown}
            aria-label="Show next slide"
            circled
            variant="text"
            css={btnStyles}
          >
            <ChevronRightIcon />
          </Button>
        </li>
      </PaginationContainer>
    </SliderWrapper>
  )
}
